/**
 * Translation between the wizard's crop config and the `CornDensityRecommendation` record.
 *
 * The management recommendation is stored as `corn_density`, the same model the retired density
 * wizard wrote, while every step, the result page and the PDF read the `activeRecommendation`
 * slice's crop config. Rather than thread a second data shape through all of those, the two
 * shapes meet here: once on the way out, once on the way back in.
 */

/** The crop-config keys this flow round-trips. Everything else on the config rides along. */
export type ManagementCropConfig = {
    crop?: string;
    products?: number[];
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    userPlanterEfficiency?: number;
    userArea?: number | string | null;
    /** The grower's habitual density, plants/ha, from the density simulator. */
    userDensity?: number | null;
    [key: string]: unknown;
};

/** `corn_density` as the API takes it — snake_case, one product. */
export type CornDensityPayload = {
    plantingDate: string;
    product_profile: number | undefined;
    climate_pattern: number | undefined;
    soil_moisture: number | undefined;
    irrigation: undefined;
    soilType: undefined;
    environment: number | undefined;
    recommended_environment: number | undefined;
    user_density: number;
    user_nitrogen: number;
    user_planter_efficiency: number | undefined;
};

/** As the API serializes it back — camelCase, and `productProfile` rather than a list. */
export type SavedCornDensity = {
    plantingDate?: string;
    productProfile?: number;
    climatePattern?: number;
    soilMoisture?: number;
    environment?: number;
    userDensity?: number;
    userNitrogen?: number;
    userPlanterEfficiency?: number;
};

/**
 * `user_density` and `user_nitrogen` are the old wizard's "compare with what the grower usually
 * does" inputs. The new flow asks for the density (the simulator's habitual density) but not the
 * nitrogen, and the columns are non-null-ish in practice, so whatever is missing is written as
 * 0 — the same value the old form defaulted them to when left untouched.
 */
const NO_USER_COMPARISON = 0;

/** Plants per hectare per unit of the API's density scale, as `useManagementData` uses it. */
const DENSITY_ANCHOR = 10000;

export const toCornDensityPayload = (cropConfig: ManagementCropConfig): CornDensityPayload => ({
    plantingDate: cropConfig.plantingDate || '',
    product_profile: cropConfig.products?.[0],
    climate_pattern: cropConfig.climatePattern,
    soil_moisture: cropConfig.soilMoisture,
    irrigation: undefined,
    soilType: undefined,
    environment: cropConfig.environment,
    recommended_environment: cropConfig.environment,
    user_density: cropConfig.userDensity || NO_USER_COMPARISON,
    user_nitrogen: NO_USER_COMPARISON,
    user_planter_efficiency: cropConfig.userPlanterEfficiency,
});

/**
 * The crop config a saved `corn_density` implies.
 *
 * Used when a stored recommendation has no crop-config snapshot to read — every recommendation
 * the retired wizard saved, which the result page still has to open. The columns carry
 * everything the sections need except the area override, which has no column; that falls back
 * to the field's own geometry, as it does for a recommendation that never set one.
 */
export const fromCornDensity = (cornDensity: SavedCornDensity, crop = 'corn'): ManagementCropConfig => ({
    crop,
    products: cornDensity.productProfile ? [cornDensity.productProfile] : [],
    plantingDate: cornDensity.plantingDate,
    environment: cornDensity.environment,
    climatePattern: cornDensity.climatePattern,
    soilMoisture: cornDensity.soilMoisture,
    userPlanterEfficiency: cornDensity.userPlanterEfficiency,
    // 0 is the "not given" placeholder above, not a density.
    userDensity: cornDensity.userDensity || undefined,
});

/**
 * The `backup` keys the retired density wizard wrote, rebuilt from what the new one has.
 *
 * The dashboard list reads the planting density for a management recommendation straight out of
 * this blob rather than re-querying, so a recommendation saved by the new wizard has to carry
 * the same shape or its row would come out blank. `cornDensityCurve` is keyed by product id and
 * holds the density in the API's own scale, which is what the list divides back out.
 */
export const toManagementBackupSnapshot = ({
    cropConfig,
    hybrid,
    product,
}: {
    cropConfig: ManagementCropConfig;
    /** The selected hybrid as the management step derives it. */
    hybrid?: { id: number; harvestDensity?: number; estimatedYield?: number };
    /** The catalogue record, for `seedsPerBag`. */
    product?: { id: number } & Record<string, unknown>;
}) => ({
    cornDensity: {
        plantingDate: cropConfig.plantingDate,
        productProfile: cropConfig.products?.[0],
        climatePattern: cropConfig.climatePattern,
        soilMoisture: cropConfig.soilMoisture,
        environment: cropConfig.environment,
        userPlanterEfficiency: cropConfig.userPlanterEfficiency,
    },
    otherData: {
        product,
        cornDensityCurve:
            hybrid && hybrid.harvestDensity
                ? {
                      [hybrid.id]: {
                          recommended: {
                              density: hybrid.harvestDensity / DENSITY_ANCHOR,
                              yield: hybrid.estimatedYield,
                          },
                      },
                  }
                : undefined,
    },
});
